import { useState } from 'react';
import type { Row, Table } from '@/lib/tanstack-table';
import {
  useFulfillmentCandidates,
  useOverrideOption,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';
import { isQuantityEditable } from '../utils/lineQuantity';
import { getCartLineTableMeta } from './cartLineTableMeta';
import {
  buildSourceOverride,
  currentSource,
  sourceChoices,
  VENDOR_SOURCE,
} from './sourceOverride';
import { TransferSourcePicker } from './TransferSourcePicker';

/**
 * Where the line ships from (GEN-3023): the cart's vendor, or a warehouse it
 * is transferred from. Picking a warehouse keeps the line in this vendor cart;
 * the transfer order is created when the cart is released, once the line is
 * approved. Changing the source clears the line's approval.
 */
export function SourceCell({
  row,
  table,
}: {
  row: Row<AssignmentLine>;
  table: Table<AssignmentLine>;
}) {
  const meta = getCartLineTableMeta(table);
  const line = row.original;
  const [open, setOpen] = useState(false);
  const override = useOverrideOption();
  // Loaded when the picker opens, not for every row on the board.
  const candidates = useFulfillmentCandidates(
    {
      productV3Key: line.productV3Key ?? '',
      destinationWarehouseId: line.destinationWarehouseId,
    },
    open,
  );
  const vendorLabel =
    meta.vendorName ||
    meta.gt('Vendor', {
      context: 'cart line source column: buy from the cart vendor',
    });
  const value = currentSource(line);
  const choices = sourceChoices(line, candidates.data?.transferSources);

  if (line.topOption?.action === 'stock') {
    return (
      <span className="text-xs text-gray-500">
        {meta.gt('Stock', {
          context: 'cart line source column: filled from on-hand stock',
        })}
      </span>
    );
  }
  const name =
    value === VENDOR_SOURCE
      ? vendorLabel
      : (line.topOption?.sourceWarehouseName ?? value);
  if (!isQuantityEditable(line) || line.productV3Key == null) {
    return <span className="truncate text-xs text-gray-500">{name}</span>;
  }

  const choose = (next: string) => {
    const vendor = candidates.data?.vendors.find(
      (candidate) => candidate.supplierId === meta.supplierId,
    );
    const result = buildSourceOverride({
      line,
      next,
      supplierId: meta.supplierId,
      vendorUnitPrice: vendor?.unitPrice,
    });
    if (result.kind === 'noop') return;
    if (result.kind === 'missing-context') {
      toast.error(
        meta.gt('Could not change the source of this line.', {
          context: 'replenishment source change error, missing price or qty',
        }),
      );
      return;
    }
    override.mutate(result.input, {
      onError: () =>
        toast.error(
          meta.gt('Could not change the source. Try again.', {
            context: 'replenishment source change error',
          }),
        ),
    });
  };

  return (
    <TransferSourcePicker
      choices={choices}
      value={value}
      open={open}
      onOpenChange={setOpen}
      onSelect={choose}
      isLoading={candidates.isLoading}
      disabled={override.isPending}
      ariaLabel={meta.gt('Ship from', {
        context: 'cart line source picker label',
      })}
      triggerLabel={name}
      triggerClassName="h-7 w-36 px-2 text-xs group-data-[density=compact]/table:h-6"
      // Going back to the vendor needs its price, which comes with the
      // candidates: not selectable until they have loaded. A transfer cart
      // has no vendor to go back to.
      leadingChoice={
        meta.supplierId == null
          ? undefined
          : {
              value: VENDOR_SOURCE,
              label: vendorLabel,
              disabled: candidates.isLoading,
            }
      }
    />
  );
}
